import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const beatSyncAccentSkill: Skill = {
    name: SKILL_NAMES.beatSyncAccent,
    kind: 'codeo',
    description:
        'Place every accent on a single regular grid so the whole graphic reads as cut to a ' +
        'rhythm — for a piece that should feel scored, even where the graphic itself is quiet.',
    instructions: `# Beat-Sync Accent

Every event in the graphic lands on a multiple of one interval. Nothing else changes — the same entrances, the same easing — but because the accents share a grid, the piece reads as *cut to something* rather than as a set of independent animations that happen to overlap.

It costs nothing and it is the difference between a graphic that feels arranged and one that feels assembled. Use it whenever a scene has more than about three events.

## Markup

Any composition works. What follows is a plain three-element scene to show the grid applied.

\`\`\`html
<div id="beat-stage" style="position: absolute; inset: 0;">
  <div id="beat-headline" style="position: absolute; left: 200px; top: 340px; width: 1300px; font-family: var(--l5-font-0), sans-serif; font-size: 116px; font-weight: 800; line-height: 1.04; color: var(--l5-color-0, #101820);">Three things changed</div>
  <div id="beat-rule" style="position: absolute; left: 200px; top: 560px; width: 420px; height: 6px; background: var(--l5-color-2, #38bdf8); transform-origin: left center;"></div>
  <div id="beat-support" style="position: absolute; left: 200px; top: 620px; font-family: var(--l5-font-0), sans-serif; font-size: 42px; font-weight: 500; color: var(--l5-color-1, #5b6472);">All of them at once</div>
  <div id="beat-pulse" style="position: absolute; inset: 0; background: var(--l5-color-2, #38bdf8);"></div>
</div>
\`\`\`

## Timeline code

Derive every position from one \`BEAT\` constant. Nothing lands between beats.

\`\`\`js
const tl = gsap.timeline({ paused: true });
// 100 BPM = 0.6s per beat. Pick from the music if there is any, or from the pace you want.
const BEAT = 0.6;
const at = (beats) => beats * BEAT;

gsap.set('#beat-headline', { y: 24, opacity: 0 });
gsap.set('#beat-rule', { scaleX: 0 });
gsap.set('#beat-support', { y: 14, opacity: 0 });
gsap.set('#beat-pulse', { opacity: 0 });

tl.to('#beat-headline', { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, at(1));
tl.to('#beat-rule', { scaleX: 1, duration: 0.4, ease: 'power3.out' }, at(3));
tl.to('#beat-support', { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out' }, at(4));

// A very quiet accent on the downbeat of the next bar keeps the tail alive.
tl.to('#beat-pulse', { opacity: 0.08, duration: 0.05, ease: 'none' }, at(8));
tl.to('#beat-pulse', { opacity: 0, duration: 0.45, ease: 'power2.out' }, at(8) + 0.05);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Choosing the interval**: with music, take it from the track (60 / BPM). Without, 0.5–0.7s per beat suits most explainer pacing.
- **Bar structure**: putting the biggest events on beats 1, 5 and 9 — the downbeats of successive bars — reads as structured rather than merely regular.
- **Off-beats for detail**: small accents on half-beats (\`at(2.5)\`) add texture without breaking the grid. Only ever for secondary elements.
- **Durations off-grid**: only the *start* positions need to land on beats. Tween durations should stay natural, and forcing them to beat multiples makes everything feel mechanical.
- **Freeze on a beat**: a hard hold on a downbeat, with everything stopping for one beat, is a powerful punctuation — see \`${SKILL_NAMES.headlineSlam}\` for what to land there.
- **Against narration**: when there is a voiceover, the narration's word onsets win. Snap to the beat only where the narration leaves you free.

## Pitfalls

- Do not put a beat grid ahead of narration timing. If a word onset and a beat disagree, the word wins — a counter landing off its spoken number is a much worse defect than an off-grid accent.
- Every timeline position must be at least 0, so \`at(0)\` is the earliest possible event; leave a beat of lead-in.
- Don't put *everything* on a beat. A graphic where all fifteen events are quantized reads as a metronome; the grid is for accents, not for every tween.
- The pulse layer covers the whole frame, so it must sit above the background and below the content in DOM order.
- Keep the full-frame accent under 0.1 opacity. A visible flash on every bar is exhausting over a whole video.
- The rule is \`scaleX\`-tweened from a left origin and must not carry a CSS \`transform\`.
- A grid does not substitute for pacing. If the scene has dead air, the beat will not fill it — see \`${SKILL_NAMES.sustainedSceneReveal}\`.`,
};
